import { FormikProps, useFormik } from 'formik'
import * as yup from 'yup'

import {
  Box,
  Button,
  Dialog,
  DialogContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import React, { ReactElement } from 'react'
import {
  AccreditationV2,
  CAPCEAccreditationV2,
  CEBrokerAccreditationV2,
  NewJerseyAccreditationV2,
  NewYorkAccreditationV2,
  VirginiaAccreditationV2,
  NotAccreditedV2,
} from 'src/v3/core/types'

export type AccreditationType = AccreditationV2['kind']

export type AccreditationsByType = {
  capce: CAPCEAccreditationV2[]
  newJersey: NewJerseyAccreditationV2[]
  ceBroker: CEBrokerAccreditationV2[]
  newYork: NewYorkAccreditationV2[]
  virginia: VirginiaAccreditationV2[]
  notAccredited: NotAccreditedV2[]
}

export const accreditationDict: Record<AccreditationType, string> = {
  capce: 'CAPCE',
  ceBroker: 'CE Broker',
  newJersey: 'New Jersey',
  newYork: 'New York',
  virginia: 'Virginia',
  notAccredited: 'Not Accredited',
}

export const accreditationTypes: AccreditationType[] = [
  'capce',
  'ceBroker',
  'newJersey',
  'newYork',
  'virginia',
  'notAccredited',
]

const AccreditationModal = <T,>({
  children,
  open,
  setOpen,
  form,
  name,
}: {
  children: ReactElement | ReactElement[]
  open: boolean
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
  form: FormikProps<T>
  name: AccreditationType
}): ReactElement => (
  <Dialog open={open} onClose={() => setOpen(false)}>
    <DialogContent>
      <Typography variant="h6" textAlign="center">
        Add {accreditationDict[name]} Accreditation
      </Typography>
      <form onSubmit={form.handleSubmit}>
        {children}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: 1,
            mt: 2,
          }}
        >
          <Button sx={{ width: 90 }} variant="contained" type="submit">
            Submit
          </Button>
          <Button
            sx={{ width: 90 }}
            variant="contained"
            color="error"
            onClick={() => setOpen(false)}
          >
            Cancel
          </Button>
        </Box>
      </form>
    </DialogContent>
  </Dialog>
)

export const CapceForm = ({
  onSubmit,
  open,
  setOpen,
}: {
  onSubmit: (accreditation: Partial<CAPCEAccreditationV2>) => void
  open: boolean
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
}): ReactElement => {
  let form = useFormik({
    validationSchema: yup.object({
      courseCode: yup.string().required(),
    }),
    initialValues: {
      courseCode: '',
    },
    onSubmit: (values) => {
      onSubmit({ courseCode: values.courseCode })
      setOpen(false)
    },
  })

  return (
    <AccreditationModal open={open} setOpen={setOpen} form={form} name="capce">
      <TextField
        fullWidth
        variant="standard"
        value={form.values.courseCode}
        label="Course Code"
        name="courseCode"
        onChange={form.handleChange}
        error={form.touched.courseCode && !!form.errors.courseCode}
      />
    </AccreditationModal>
  )
}

export const CeBrokerForm = ({
  onSubmit,
  open,
  setOpen,
  exclude,
}: {
  onSubmit: (accreditation: Partial<CEBrokerAccreditationV2>) => void
  open: boolean
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
  exclude?: string[]
}): ReactElement => {
  let form = useFormik({
    validationSchema: yup.object({
      courseCode: yup.string().required(),
      professionCode: yup.string().required(),
      subjectArea: yup.string().required(),
      state: yup.string().required(),
    }),
    initialValues: {
      courseCode: '',
      professionCode: '',
      subjectArea: '',
      state: '',
    },
    onSubmit: (values, { resetForm }) => {
      onSubmit({
        courseCode: values.courseCode,
        professionCode: values.professionCode,
        subjectArea: values.subjectArea,
        state: values.state,
      })
      setOpen(false)
      resetForm()
    },
  })

  return (
    <AccreditationModal
      open={open}
      setOpen={setOpen}
      form={form}
      name="ceBroker"
    >
      <TextField
        fullWidth
        variant="standard"
        value={form.values.courseCode}
        label="Course Code"
        name="courseCode"
        onChange={form.handleChange}
        error={form.touched.courseCode && !!form.errors.courseCode}
      />
      <TextField
        fullWidth
        variant="standard"
        value={form.values.professionCode}
        label="Profession Code"
        name="professionCode"
        onChange={form.handleChange}
        error={form.touched.professionCode && !!form.errors.professionCode}
      />
      <TextField
        fullWidth
        variant="standard"
        value={form.values.subjectArea}
        label="Subject Area"
        name="subjectArea"
        onChange={form.handleChange}
        error={form.touched.subjectArea && !!form.errors.subjectArea}
      />
      <FormControl fullWidth variant="standard">
        <InputLabel>State</InputLabel>
        <Select
          value={form.values.state}
          label="State"
          name="state"
          onChange={form.handleChange}
          error={form.touched.state && !!form.errors.state}
        >
          {!exclude?.includes('Florida') && (
            <MenuItem value="Florida">Florida</MenuItem>
          )}
          {!exclude?.includes('Kentucky') && (
            <MenuItem value="Kentucky">Kentucky</MenuItem>
          )}
          {!exclude?.includes('Tennessee') && (
            <MenuItem value="Tennessee">Tennessee</MenuItem>
          )}
        </Select>
      </FormControl>
    </AccreditationModal>
  )
}

export const NewJerseyForm = ({
  onSubmit,
  open,
  setOpen,
}: {
  onSubmit: (accreditation: Partial<NewJerseyAccreditationV2>) => void
  open: boolean
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
}): ReactElement => {
  let form = useFormik({
    validationSchema: yup.object({
      courseCode: yup.string().required(),
    }),
    initialValues: {
      courseCode: '',
    },
    onSubmit: (values) => {
      onSubmit({ courseCode: values.courseCode })
      setOpen(false)
    },
  })

  return (
    <AccreditationModal
      open={open}
      setOpen={setOpen}
      form={form}
      name="newJersey"
    >
      <TextField
        fullWidth
        variant="standard"
        value={form.values.courseCode}
        label="Course Code"
        name="courseCode"
        onChange={form.handleChange}
        error={form.touched.courseCode && !!form.errors.courseCode}
      />
    </AccreditationModal>
  )
}

export const NewYorkForm = ({
  onSubmit,
  open,
  setOpen,
}: {
  onSubmit: (accreditation: Partial<NewYorkAccreditationV2>) => void
  open: boolean
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
}): ReactElement => {
  let form = useFormik({
    validationSchema: yup.object({
      topicArea: yup.string().required(),
    }),
    initialValues: {
      topicArea: '',
    },
    onSubmit: (values) => {
      onSubmit({ topicArea: values.topicArea })
      setOpen(false)
    },
  })

  return (
    <AccreditationModal
      open={open}
      setOpen={setOpen}
      form={form}
      name="newYork"
    >
      <FormControl fullWidth variant="standard">
        <InputLabel>Topic Area</InputLabel>
        <Select
          value={form.values.topicArea}
          label="Topic Area"
          name="topicArea"
          onChange={form.handleChange}
          error={form.touched.topicArea && !!form.errors.topicArea}
        >
          <MenuItem value="Preparatory">Preparatory</MenuItem>
          <MenuItem value="Airway">Airway</MenuItem>
          <MenuItem value="Pharmacology | Med Admin | Emergency Meds">
            Pharmacology | Med Admin | Emergency Meds
          </MenuItem>
          <MenuItem value="Immunology">Immunology</MenuItem>
          <MenuItem value="Toxicology">Toxicology</MenuItem>
          <MenuItem value="Endocrine">Endocrine</MenuItem>
          <MenuItem value="Neurology">Neurology</MenuItem>
          <MenuItem value="Abdominal | Geni-Renal | GI | Hematology">
            Abdominal | Geni-Renal | GI | Hematology
          </MenuItem>
          <MenuItem value="Respiratory">Respiratory</MenuItem>
          <MenuItem value="Psychiatric">Psychiatric</MenuItem>
          <MenuItem value="Cardiology">Cardiology</MenuItem>
          <MenuItem value="Shock and Resuscitation">
            Shock and Resuscitation
          </MenuItem>
          <MenuItem value="Trauma">Trauma</MenuItem>
          <MenuItem value="Geriatrics">Geriatrics</MenuItem>
          <MenuItem value="OB | Neonate | Pediatrics">
            OB | Neonate | Pediatrics
          </MenuItem>
          <MenuItem value="Special Needs Pt">Special Needs Pt</MenuItem>
          <MenuItem value="EMS Operations">EMS Operations</MenuItem>
          <MenuItem value="Mental Health of EMT">Mental Health of EMT</MenuItem>
          <MenuItem value="Patient Lifting and Moving">
            Patient Lifting and Moving
          </MenuItem>
          <MenuItem value="Pediatric Safe Transport">
            Pediatric Safe Transport
          </MenuItem>
          <MenuItem value="Emergency Vehicle Driver Training">
            Emergency Vehicle Driver Training
          </MenuItem>
        </Select>
      </FormControl>
    </AccreditationModal>
  )
}

export const VirginiaForm = ({
  onSubmit,
  open,
  setOpen,
}: {
  onSubmit: (accreditation: Partial<VirginiaAccreditationV2>) => void
  open: boolean
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
}): ReactElement => {
  let form = useFormik({
    validationSchema: yup.object({
      topic: yup.string().required(),
      subTopic: yup.string().required(),
      areaNumberALS: yup.string(),
      areaNumberBLS: yup.string(),
    }),
    initialValues: {
      topic: '',
      subTopic: '',
      areaNumberALS: '',
      areaNumberBLS: '',
    },
    onSubmit: (values) => {
      onSubmit({
        topic: values.topic,
        subTopic: values.subTopic,
        areaNumberALS: values.areaNumberALS,
        areaNumberBLS: values.areaNumberBLS,
      })
      setOpen(false)
    },
  })

  return (
    <AccreditationModal
      open={open}
      setOpen={setOpen}
      form={form}
      name="virginia"
    >
      <TextField
        fullWidth
        variant="standard"
        value={form.values.topic}
        label="Topic"
        name="topic"
        onChange={form.handleChange}
        error={form.touched.topic && !!form.errors.topic}
      />
      <TextField
        fullWidth
        variant="standard"
        value={form.values.subTopic}
        label="Sub-Topic"
        name="subTopic"
        onChange={form.handleChange}
        error={form.touched.subTopic && !!form.errors.subTopic}
      />
      <TextField
        fullWidth
        variant="standard"
        value={form.values.areaNumberALS}
        label="Area Number ALS"
        name="areaNumberALS"
        onChange={form.handleChange}
        error={form.touched.areaNumberALS && !!form.errors.areaNumberALS}
      />
      <TextField
        fullWidth
        variant="standard"
        value={form.values.areaNumberBLS}
        label="Area Number BLS"
        name="areaNumberBLS"
        onChange={form.handleChange}
        error={form.touched.areaNumberBLS && !!form.errors.areaNumberBLS}
      />
    </AccreditationModal>
  )
}

export const NotAccreditedForm = ({
  onSubmit,
  open,
  setOpen,
}: {
  onSubmit: (accreditation: Partial<NotAccreditedV2>) => void
  open: boolean
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
}): ReactElement => {
  let form = useFormik({
    validationSchema: yup.object({
      courseCode: yup.string().required(),
    }),
    initialValues: {
      courseCode: '',
    },
    onSubmit: (values) => {
      onSubmit({
        courseCode: values.courseCode,
      })
      setOpen(false)
    },
  })

  return (
    <AccreditationModal
      open={open}
      setOpen={setOpen}
      form={form}
      name="notAccredited"
    >
      <TextField
        fullWidth
        variant="standard"
        value={form.values.courseCode}
        label="Course Code"
        name="courseCode"
        onChange={form.handleChange}
        error={form.touched.courseCode && !!form.errors.courseCode}
      />
    </AccreditationModal>
  )
}
